import { storedTime } from './storedTime';
/**
 * 보관한 실행계획의 파일 형식 — **읽고 쓰는 유일한 곳.**
 *
 * `.livesqlplan`(JSON)은 사용자가 주고받는 파일이라 빌드가 갈린 두 사람 사이를 오간다.
 * `projectFile.js` 와 같은 규칙을 따른다(docs/schema-compat.md):
 *  - 형식 버전은 `version` 한 필드. 없으면 최초 형식으로 본다.
 *  - **모르는 필드를 버리지 않는다.** 최상위도 항목 안쪽도 그대로 실어 나른다.
 *  - 이 빌드보다 새 형식이면 **추측해서 고치지 않는다.** 버전도 끌어내리지 않는다.
 *  - 비교는 문자열이 아니라 **숫자 튜플** — '1.10' 이 '1.9' 보다 낮게 읽히면 안 된다.
 *
 * `.md` 는 사람이 읽는 쪽이다(티켓·메일에 붙인다). 되읽지 않으므로 버전을 두지 않는다.
 */

export const PLAN_FILE_SCHEMA_VERSION = '1.0';
export const PLAN_FILE_TYPE = 'livesql-plan-archive';

/** 이 PC·이 계정에서만 뜻이 있는 값 — 파일에 쓰지 않는다. */
const RUNTIME_ONLY = ['usrid', 'id', 'groupId', 'captureId', 'connectionId'];

/** 점 구분 버전 비교. 같으면 0, a 가 낮으면 음수. */
export function cmpVersion(a, b) {
    const parse = (v) => String(v || PLAN_FILE_SCHEMA_VERSION).split('.').map(n => parseInt(n, 10) || 0);
    const [x, y] = [parse(a), parse(b)];
    for (let i = 0; i < Math.max(x.length, y.length); i++) {
        const d = (x[i] || 0) - (y[i] || 0);
        if (d !== 0) return d;
    }
    return 0;
}

const stripRuntime = (obj) => {
    const out = { ...obj };
    for (const k of RUNTIME_ONLY) delete out[k];
    return out;
};

/**
 * 보관물 → 파일 본문(JSON 문자열).
 * @param group `{ title, kind, summary, verdict, note, createdAt, items[], ...carry }`
 */
export function serializePlanArchive(group) {
    const g = group || {};
    const { items = [], carry = {}, ...rest } = g;
    const body = {
        // 모르는 최상위 필드를 먼저 깔고 우리가 아는 것으로 덮는다 — 왕복이 무손실이어야 한다
        ...carry,
        ...stripRuntime(rest),
        type: PLAN_FILE_TYPE,
        version: cmpVersion(g.version, PLAN_FILE_SCHEMA_VERSION) > 0 ? g.version : PLAN_FILE_SCHEMA_VERSION,
        items: items.map(stripRuntime),
    };
    return JSON.stringify(body, null, 2);
}

/**
 * 파일 본문 → 보관물.
 * @returns `{ group, docVersion, tooNew }` — `tooNew` 면 화면이 알리고 **고쳐 쓰지 않는다**
 */
export function parsePlanArchive(text) {
    let doc;
    try {
        doc = JSON.parse(text);
    } catch (_e) {
        throw new Error('PLAN_FILE_PARSE');
    }
    if (!doc || typeof doc !== 'object' || doc.type !== PLAN_FILE_TYPE) {
        throw new Error('PLAN_FILE_TYPE');
    }
    const docVersion = doc.version || PLAN_FILE_SCHEMA_VERSION;
    const tooNew = cmpVersion(docVersion, PLAN_FILE_SCHEMA_VERSION) > 0;

    const { items, type, version, ...carry } = doc;
    return {
        group: {
            ...carry,
            items: Array.isArray(items) ? items : [],
            // 읽을 때 챙겨 둔 것을 그대로 돌려줘야 다음 저장에서 살아남는다
            carry,
            version: docVersion,
        },
        docVersion,
        tooNew,
    };
}

const fence = (s) => `\`\`\`\n${String(s == null ? '' : s).replace(/```/g, '\\`\\`\\`')}\n\`\`\``;

/** 수치를 사람 글로 — 없는 값은 0 이 아니라 '-' 다(작은 값이 0 으로 뭉개지지 않게) */
const num = (v) => (v == null || Number.isNaN(Number(v)) ? '-' : Number(v).toLocaleString());

/** 보관물 → 사람이 읽는 마크다운 */
export function planArchiveToMarkdown(group) {
    const g = group || {};
    const items = g.items || [];
    const out = [`# ${g.title || 'Execution plan'}`, ''];

    const head = [];
    if (g.createdAt) head.push(`- Saved: ${storedTime(g.createdAt)}`);
    const first = items[0] || {};
    if (first.connName) head.push(`- Connection: ${first.connName}${first.dbType ? ` (${first.dbType})` : ''}`);
    if (g.summary) head.push(`- Summary: ${g.summary}`);
    if (g.note) head.push(`- Note: ${g.note}`);
    if (head.length) out.push(...head, '');

    for (const it of items) {
        const label = it.label || it.role || 'plan';
        out.push(`## ${label}`, '');
        if (it.sql) out.push('### SQL', '', `\`\`\`sql\n${it.sql}\n\`\`\``, '');
        if (it.metrics) {
            const m = it.metrics;
            out.push('### Metrics', '',
                `| buffers | rows | elapsed(s) | cost |`,
                `|---|---|---|---|`,
                `| ${num(m.buffers)} | ${num(m.actualRows != null ? m.actualRows : m.estimatedRows)} | ${num(m.elapsedSec)} | ${num(m.cost)} |`,
                '');
        }
        out.push('### Plan', '', fence(it.planText), '');
        if (it.monitorReport) out.push('### SQL Monitor Report', '', fence(it.monitorReport), '');
    }

    if (g.verdict) {
        out.push('## Verdict', '', fence(JSON.stringify(g.verdict, null, 2)), '');
    }
    return out.join('\n');
}

export default { serializePlanArchive, parsePlanArchive, planArchiveToMarkdown, cmpVersion };
